<template>
    <div class="flex mt-[42px] mb-10 justify-between">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.alerts") }}
        </h1>
    </div>

    <div>
        <BasicTabs :tabs="tabs" :tab-key="'alerts/overview'" />
    </div>

    <!-- Upgrade Required Section -->
    <div v-if="!hasAlertsAccess">
        <InfoCard
            :img-src="lockedNotAuthorized"
            img-alt="User has no access to content"
            :title="t('vue.upgrade_required')"
            :body-text="t('vue.not_having_access_message')"
            :show-dot="false"
            :disable-top-margin="false"
            :button-label="t('vue.upgrade_my_plan')"
            :button-href="route('billing.subscriptions.index')"
        />
    </div>

    <div
        v-else-if="!props.notificationsEnabled && hasAlertsAccess"
        class="mt-[42px]"
    >
        <InfoCard
            :img-src="alertsTurnedOffCheck"
            img-alt="No alerts active"
            :title="t('vue.alerts_turned_off')"
            :body-text="t('vue.alerts_turned_off_desc')"
            :show-dot="false"
            :disable-top-margin="true"
            :custom-width="119"
            :custom-height="119"
        >
            <template #actions>
                <AtomicButton
                    color="yellow"
                    size="md"
                    class="h-8 px-4 text-[12px] font-semibold leading-4"
                    @click="enableAlerts"
                >
                    {{ t("vue.enable_alerts") }}
                </AtomicButton>
            </template>
        </InfoCard>
    </div>

    <div
        v-else-if="
            props.notificationsEnabled && !notificationsExist && hasAlertsAccess
        "
        class="mt-[42px]"
    >
        <InfoCard
            :img-src="noAlertsCheck"
            img-alt="No alerts detected"
            :title="t('vue.no_alerts_detected')"
            :body-text="t('vue.no_alerts_detected_desc')"
            :show-dot="false"
            :disable-top-margin="true"
            :custom-width="119"
            :custom-height="119"
        >
        </InfoCard>
    </div>

    <div
        v-else-if="
            props.notificationsEnabled && notificationsExist && hasAlertsAccess
        "
        class="mt-[42px]"
    >
        <DynamicContentTable
            :title="t('vue.domains')"
            :subtitle="t('vue.alerts_table_sub_header')"
            :search-enabled="false"
            :rows="props.domains"
            :columns="columns"
            :endpoint="() => route('alerts.overview')"
            :base-query="{}"
            :table="props.table"
            :pagination="props.pagination"
            empty-text="No results"
            :per-page-options="[10, 25, 50, 100]"
            per-page-label="per page"
            :row-key="(r: any) => r.domain"
            :row-link="(row) => route('reports.show', { domain: row.domain })"
        >
            <template #row="{ row: domain }">
                <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                        <div
                            :class="[
                                'h-2 w-2 rounded-full',
                                domain.active ? 'bg-green-500' : 'bg-gray-400',
                            ]"
                        ></div>
                        <span class="font-[500] text-gray-600">{{
                            domain.domain
                        }}</span>
                    </div>
                </td>

                <td class="px-6 py-4">
                    <span
                        :class="[
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            domain.active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800',
                        ]"
                    >
                        {{ domain.active ? "Active" : "Inactive" }}
                    </span>
                </td>

                <td class="px-6 py-4">
                    <div
                        v-if="domain.current_compliance !== undefined"
                        class="flex items-center gap-2"
                    >
                        <span
                            :class="[
                                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                domain.current_compliance >= 80
                                    ? 'bg-green-100 text-green-800'
                                    : domain.current_compliance >= 50
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800',
                            ]"
                        >
                            {{ domain.current_compliance }}%
                        </span>
                        <span class="text-xs text-gray-500"
                            >({{ domain.report_30?.sum || 0 }} emails)</span
                        >
                    </div>
                    <span v-else class="text-sm text-gray-400">No data</span>
                </td>

                <td class="px-6 py-4">
                    <div
                        v-if="domain.previous_compliance !== undefined"
                        class="flex items-center gap-2"
                    >
                        <span
                            :class="[
                                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                domain.previous_compliance >= 80
                                    ? 'bg-green-100 text-green-800'
                                    : domain.previous_compliance >= 50
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800',
                            ]"
                        >
                            {{ domain.previous_compliance }}%
                        </span>
                        <span class="text-xs text-gray-500"
                            >({{
                                domain.report_30_before?.sum || 0
                            }}
                            emails)</span
                        >
                    </div>
                    <span v-else class="text-sm text-gray-400">No data</span>
                </td>

                <td class="px-6 py-4">
                    <div
                        v-if="
                            domain.compliance_change !== null &&
                            domain.compliance_change !== undefined
                        "
                        class="flex items-center gap-1"
                    >
                        <component
                            :is="
                                domain.compliance_change > 0
                                    ? TrendingUp
                                    : domain.compliance_change < 0
                                      ? TrendingDown
                                      : Minus
                            "
                            :class="[
                                'h-4 w-4',
                                domain.compliance_change > 0
                                    ? 'text-green-600'
                                    : domain.compliance_change < 0
                                      ? 'text-red-600'
                                      : 'text-gray-400',
                            ]"
                        />
                        <span
                            :class="[
                                'text-sm font-medium',
                                domain.compliance_change > 0
                                    ? 'text-green-600'
                                    : domain.compliance_change < 0
                                      ? 'text-red-600'
                                      : 'text-gray-600',
                            ]"
                        >
                            {{ domain.compliance_change > 0 ? "+" : ""
                            }}{{ domain.compliance_change }}%
                        </span>
                    </div>
                    <span v-else class="text-sm text-gray-400">No data</span>
                </td>
            </template>
        </DynamicContentTable>
    </div>

    <TrialEndedPopup />
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import useAlertsAccess from "@/composables/useAlertsAccess";
import {
    Bell,
    Settings,
    TrendingUp,
    TrendingDown,
    Minus,
} from "lucide-vue-next";

import type TabItem from "@/types/Settings/TabItem";
import type IDomain from "@/types/Domains/IDomain";
import { useTranslations } from "@/composables/useTranslations.ts";
import route from "ziggy-js";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { computed } from "vue";
import useEnableAlerts from "@/composables/useEnableAlerts";

const { enableAlerts } = useEnableAlerts();

const { hasAlertsAccess } = useAlertsAccess();

const { t } = useTranslations();

const lockedNotAuthorized = new URL(
    "/resources/js/assets/images/locked-not-authorized.svg",
    import.meta.url,
).href;

const noAlertsCheck = new URL(
    "/resources/js/assets/images/speech-bubble-check.svg",
    import.meta.url,
).href;

const alertsTurnedOffCheck = new URL(
    "/resources/js/assets/images/empty-speech-bubble-check.svg",
    import.meta.url,
).href;

interface ReportNotification {
    id?: number;
    name?: string;
    description?: string;
    frequency?: string;
    is_active?: boolean | number; // v DB máš 0/1
    team_id?: number;
    created_by?: number;
    last_sent_at?: string | null;
    next_send_at?: string | null;
    settings?: string | Record<string, unknown>;
    created_at?: string;
    updated_at?: string;
}

type SortDir = "asc" | "desc";

interface Props {
    domains: IDomain[];
    notifications?: ReportNotification[];
    notificationsEnabled: boolean;

    table: {
        sort_by?: string | null;
        sort_dir?: SortDir | null;
        per_page?: number;
        current_page?: number;
        q?: string;
    };

    pagination: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
        from: number | null;
        to: number | null;
    };
}

const columns = [
    {
        key: "domain",
        label: "Domain",
        colClass: "w-[28%]",
        sortable: true,
        sortKey: "domain",
    },
    {
        key: "status",
        label: "Status",
        colClass: "w-[14%]",
        sortable: true,
        sortKey: "active",
    },
    {
        key: "current",
        label: "Current Compliance",
        colClass: "w-[22%]",
        sortable: true,
        sortKey: "current_compliance",
    },
    {
        key: "previous",
        label: "Previous Compliance",
        colClass: "w-[22%]",
        sortable: true,
        sortKey: "previous_compliance",
    },
    {
        key: "change",
        label: "Change",
        colClass: "w-[14%]",
        sortable: true,
        sortKey: "compliance_change",
    },
];

const props = defineProps<Props>();

const notifications = computed(() => props.notifications ?? []);

const notificationsExist = computed(() => notifications.value.length > 0);

const tabs: TabItem[] = [
    {
        title: t("vue.overview"),
        key: "alerts/overview",
        icon: Bell,
        url: "/alerts/overview",
    },
    {
        title: t("vue.settings"),
        key: "settings",
        icon: Settings,
        url: "/alerts/settings",
    },
];
</script>
